/**
 * Timecode helpers shared by the annotation studio, the editor workspace and
 * the client-side marker exports.
 *
 * Times are stored as seconds (the annotation and task data model). Frame
 * numbers are counted from zero; a time inside a frame belongs to that frame,
 * so `formatTimecode` floors rather than rounds. 29.97 and 59.94 use
 * drop-frame timecode (HH:MM:SS;FF), the way every NLE labels those rates, so
 * a note shown here at 00:10:00;00 lands on 00:10:00;00 in the editor's
 * sequence instead of drifting 18 frames over ten minutes.
 */

export const DEFAULT_FPS = 30;

/** Rates offered when the source frame rate is not known. */
export const COMMON_FRAME_RATES = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60] as const;

/** Tolerance for float noise such as 0.1 * 30 = 2.9999999999999996. */
const EPSILON = 1e-6;

const pad = (value: number, length = 2) => String(Math.max(0, Math.floor(value))).padStart(length, '0');

/** A usable frame rate: positive and finite, otherwise the default. */
export const normalizeFps = (fps?: number | null): number =>
  typeof fps === 'number' && Number.isFinite(fps) && fps > 0 ? fps : DEFAULT_FPS;

/** Frames per timecode second: 30 for 29.97, 24 for 23.976. */
export const nominalFps = (fps?: number | null): number => Math.max(1, Math.round(normalizeFps(fps)));

/** 29.97 and 59.94 are labelled with drop-frame timecode. */
export const isDropFrameRate = (fps?: number | null): boolean => {
  const rate = normalizeFps(fps);
  return Math.abs(rate - 29.97) < 0.01 || Math.abs(rate - 59.94) < 0.01;
};

/** Human label for a rate: 29.97, 23.976, 30. */
export const formatFrameRate = (fps?: number | null): string => {
  const rate = normalizeFps(fps);
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};

const finiteSeconds = (seconds: number) => (Number.isFinite(seconds) ? Math.max(0, seconds) : 0);

/** The zero-based frame that is showing at `seconds`. */
export const secondsToFrames = (seconds: number, fps?: number | null): number =>
  Math.floor(finiteSeconds(seconds) * normalizeFps(fps) + EPSILON);

/** Start of frame `frame` in seconds. */
export const framesToSeconds = (frame: number, fps?: number | null): number =>
  Math.max(0, frame) / normalizeFps(fps);

/**
 * Where to seek to show `frame`. Seeking to the exact frame boundary can land
 * on the previous frame after float rounding in the decoder, so aim for the
 * middle of the frame; `secondsToFrames` still reads it back as `frame`.
 */
export const frameSeekTime = (frame: number, fps?: number | null): number =>
  (Math.max(0, Math.floor(frame)) + 0.5) / normalizeFps(fps);

/** The time `delta` frames from `seconds`, clamped to [0, duration]. */
export const stepFrames = (seconds: number, delta: number, fps?: number | null, duration?: number): number => {
  const rate = normalizeFps(fps);
  let frame = secondsToFrames(seconds, rate) + Math.trunc(delta);
  if (frame < 0) frame = 0;
  if (typeof duration === 'number' && Number.isFinite(duration) && duration > 0) {
    const lastFrame = Math.max(0, Math.ceil(duration * rate - EPSILON) - 1);
    if (frame > lastFrame) frame = lastFrame;
  }
  return frameSeekTime(frame, rate);
};

/** Snap a time to the start of its frame. */
export const snapToFrame = (seconds: number, fps?: number | null): number =>
  framesToSeconds(secondsToFrames(seconds, fps), fps);

interface TimecodeOptions {
  /** Defaults to true for 29.97 / 59.94. */
  dropFrame?: boolean;
  /** Leave out the hours field when it is zero ("MM:SS:FF"). */
  omitZeroHours?: boolean;
}

/** Frame count -> timecode parts, applying drop-frame numbering when asked. */
const frameCountToParts = (frames: number, fps: number, dropFrame: boolean) => {
  const base = nominalFps(fps);
  let count = Math.max(0, Math.floor(frames));
  if (dropFrame) {
    // SMPTE drop-frame: skip frame numbers 0 and 1 (2 and 3 at 59.94) at the
    // start of every minute except each tenth minute.
    const dropPerMinute = Math.round(base / 15);
    const framesPerTenMinutes = Math.round(fps * 600);
    const framesPerMinute = base * 60 - dropPerMinute;
    const tens = Math.floor(count / framesPerTenMinutes);
    const remainder = count % framesPerTenMinutes;
    count += dropPerMinute * 9 * tens;
    if (remainder > dropPerMinute) {
      count += dropPerMinute * Math.floor((remainder - dropPerMinute) / framesPerMinute);
    }
  }
  const ff = count % base;
  const totalSeconds = Math.floor(count / base);
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor(totalSeconds / 60) % 60,
    seconds: totalSeconds % 60,
    frames: ff,
  };
};

/** Frame count -> "HH:MM:SS:FF" ("HH:MM:SS;FF" for drop-frame). */
export const framesToTimecode = (frames: number, fps?: number | null, options: TimecodeOptions = {}): string => {
  const rate = normalizeFps(fps);
  const dropFrame = options.dropFrame ?? isDropFrameRate(rate);
  const parts = frameCountToParts(frames, rate, dropFrame);
  const frameDigits = nominalFps(rate) > 100 ? 3 : 2;
  const separator = dropFrame ? ';' : ':';
  const body = `${pad(parts.minutes)}:${pad(parts.seconds)}${separator}${pad(parts.frames, frameDigits)}`;
  if (options.omitZeroHours && parts.hours === 0) return body;
  return `${pad(parts.hours)}:${body}`;
};

/** Seconds -> "HH:MM:SS:FF" at `fps` (30 by default). */
export const formatTimecode = (seconds: number, fps: number = DEFAULT_FPS, options: TimecodeOptions = {}): string =>
  framesToTimecode(secondsToFrames(seconds, fps), fps, options);

/** Seconds -> "m:ss.s" under an hour, "h:mm:ss" from an hour. */
export const formatClock = (seconds: number): string => {
  const safe = finiteSeconds(seconds);
  if (safe >= 3600) {
    const total = Math.floor(safe + EPSILON);
    return `${Math.floor(total / 3600)}:${pad(Math.floor(total / 60) % 60)}:${pad(total % 60)}`;
  }
  const tenths = Math.floor(safe * 10 + EPSILON);
  const minutes = Math.floor(tenths / 600);
  const secs = Math.floor(tenths / 10) % 60;
  return `${minutes}:${pad(secs)}.${tenths % 10}`;
};

/** Seconds -> "4.2s", "1m 05s", "1h 02m" for durations and estimates. */
export const formatDuration = (seconds: number): string => {
  const safe = finiteSeconds(seconds);
  if (safe < 60) {
    const rounded = Math.round(safe * 10) / 10;
    return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}s`;
  }
  const total = Math.round(safe);
  if (total < 3600) return `${Math.floor(total / 60)}m ${pad(total % 60)}s`;
  return `${Math.floor(total / 3600)}h ${pad(Math.floor(total / 60) % 60)}m`;
};

/** Drop-frame timecode parts -> frame count. */
const partsToFrameCount = (
  hours: number,
  minutes: number,
  seconds: number,
  frames: number,
  fps: number,
  dropFrame: boolean
) => {
  const base = nominalFps(fps);
  const totalMinutes = hours * 60 + minutes;
  let count = (totalMinutes * 60 + seconds) * base + frames;
  if (dropFrame) {
    const dropPerMinute = Math.round(base / 15);
    count -= dropPerMinute * (totalMinutes - Math.floor(totalMinutes / 10));
  }
  return count;
};

/**
 * Parses what a person types into a time field. Returns seconds, or null when
 * the text is not a valid time.
 *
 *  - "01:02:03:04" / "01:02:03;04" / "02:03:04:05" — timecode (4 fields, frames last)
 *  - "1:02:03" — h:mm:ss (3 fields)
 *  - "2:03.5" — m:ss(.s)
 *  - "12.5" — seconds
 */
export const parseTimecode = (input: string, fps: number = DEFAULT_FPS): number | null => {
  const text = String(input ?? '').trim();
  if (!text) return null;
  const rate = normalizeFps(fps);

  if (/^\d+(\.\d+)?$/.test(text)) {
    const value = Number(text);
    return Number.isFinite(value) ? value : null;
  }

  const fields = text.split(/[:;]/);
  if (fields.some((field) => field === '')) return null;

  if (fields.length === 4) {
    if (!fields.every((field) => /^\d+$/.test(field))) return null;
    const [hours, minutes, seconds, frames] = fields.map(Number);
    if (minutes > 59 || seconds > 59 || frames >= nominalFps(rate)) return null;
    // Drop-frame numbering is a property of the rate, whichever separator was typed.
    return partsToFrameCount(hours, minutes, seconds, frames, rate, isDropFrameRate(rate)) / rate;
  }

  if (fields.length === 3 || fields.length === 2) {
    const last = fields[fields.length - 1];
    if (!/^\d+(\.\d+)?$/.test(last)) return null;
    const leading = fields.slice(0, -1);
    if (!leading.every((field) => /^\d+$/.test(field))) return null;
    const secs = Number(last);
    if (secs >= 60) return null;
    if (fields.length === 2) {
      return Number(leading[0]) * 60 + secs;
    }
    const [hours, minutes] = leading.map(Number);
    if (minutes > 59) return null;
    return hours * 3600 + minutes * 60 + secs;
  }

  return null;
};
